/**
 * EmployeeMatrixView.tsx
 * Employee Schedule team view — L1 redesign (2026-07-25, design rounds 1–11).
 *
 * A flat list of people (no tier bands): each row shows a busy pill per week
 * with tiny teal / violet / orange ticks (weekend work · worked stat · a day
 * over 100%). Clicking a row opens an F1 ACCORDION in place — the row's cells
 * morph into 7-day load bars (height = Σ day-share %, dashed 100% line) and
 * the person's flat task plan (nickname chip + task + bars, drag-to-extend)
 * slides open beneath. Several people can be open at once; clicking again (or
 * CLOSE ✕) slides shut. The v1.1.39 full-page focus mode is retired — this
 * accordion replaces it, and the same row set is the pattern for Scenario
 * Planning staffing.
 *
 * All day figures derive from the assignment bars via utils/personDayLoad —
 * the summary can never disagree with the plan beneath it.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { Phase2RailScrollArrows } from './scenario/Phase2RailScrollArrows';
import { EmployeeWeekLoadRow, LOAD_NAME_W, LOAD_WEEK_W, type WeekPillStyle } from '../../../components/schedule/EmployeeWeekLoadRow';
import { EmployeeAccordionPlan } from '../../../components/schedule/EmployeeAccordionPlan';
import { weekOfMondayLabel, addDaysISO } from '../../../components/schedule/employeeScheduleShared';
import { buildPersonWeekLoads } from '../../../utils/personDayLoad';
import { TeamWeekForecastStrip, FORECAST_STRIP_H } from '../../../components/schedule/TeamWeekForecastStrip';
import { computeTeamWeekForecast, isFlaggedWeek, type TeamWeekForecast } from '../../../utils/teamWeekForecast';
import type { EmployeeBar, EmployeeBarUser, EmployeeWeekCapacityCell } from '../../../services/scheduleEmployeeBars';
import { capacityMapKey } from '../../../hooks/useEmployeeScheduleData';

export interface EmployeeMatrixViewProps {
  users: EmployeeBarUser[];
  bars: EmployeeBar[];
  capacityByKey: Map<string, EmployeeWeekCapacityCell>;
  weekStarts: string[];
  /** Deep-link seed (?focusUserKey=…) — that person's accordion starts open. */
  expandedUserKey: string | null;
  /** Kept for the page's URL bookkeeping — reports the LAST toggled person. */
  onToggleExpand: (userKey: string) => void;
  onBarClick: (bar: EmployeeBar, anchor: HTMLElement) => void;
  /** P-22: stat-holiday dates — violet day bars + holiday-aware busy %. */
  holidays?: ReadonlySet<string>;
  /**
   * Grow the data window by one week (2026-07-26): the ▶ edge arrow first
   * scrolls through what's loaded, then LOADS another week each click once at
   * the right edge — so the horizon extends as far as the planner wants.
   */
  onExtendWeeks?: () => void;
}

export const EmployeeMatrixView: React.FC<EmployeeMatrixViewProps> = ({
  users, bars, capacityByKey, weekStarts,
  expandedUserKey, onToggleExpand, onBarClick, holidays, onExtendWeeks,
}) => {
  const theme = useTheme();

  // Round-12 polish: two shipped pill styles — 'tile' (full-cell, heat tint,
  // the default) and 'barfill' (track whose fill length is the busy %). The
  // pick persists per browser.
  const [pillStyle, setPillStyle] = useState<WeekPillStyle>(() => {
    try {
      const v = window.localStorage.getItem('employeeSchedule.pillStyle');
      return v === 'barfill' ? 'barfill' : 'tile';
    } catch { return 'tile'; }
  });
  const changePillStyle = (next: WeekPillStyle | null) => {
    if (!next) return;
    setPillStyle(next);
    try { window.localStorage.setItem('employeeSchedule.pillStyle', next); } catch { /* private mode */ }
  };

  const barsByUser = useMemo(() => {
    const m = new Map<string, EmployeeBar[]>();
    for (const b of bars) { const a = m.get(b.userKey) ?? []; a.push(b); m.set(b.userKey, a); }
    return m;
  }, [bars]);

  // ── F1 accordion: several people may be open at once ────────────────────
  const [openKeys, setOpenKeys] = useState<Set<string>>(
    () => new Set(expandedUserKey ? [expandedUserKey] : []),
  );
  // Re-seed when a deep link lands after mount (page sets it from the URL).
  const seeded = useRef(expandedUserKey);
  useEffect(() => {
    if (expandedUserKey && expandedUserKey !== seeded.current) {
      seeded.current = expandedUserKey;
      setOpenKeys((prev) => new Set(prev).add(expandedUserKey));
    }
  }, [expandedUserKey]);
  const toggle = (userKey: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      next.has(userKey) ? next.delete(userKey) : next.add(userKey);
      return next;
    });
    onToggleExpand(userKey);
  };

  // ── PR3 team forecast (unchanged — rolled up over the whole team) ────────
  const forecast = useMemo<TeamWeekForecast[]>(
    () => computeTeamWeekForecast([...capacityByKey.values()], weekStarts),
    [capacityByKey, weekStarts],
  );
  const forecastByWeek = useMemo(() => {
    const m = new Map<string, TeamWeekForecast>();
    for (const f of forecast) m.set(f.weekStart, f);
    return m;
  }, [forecast]);
  const flaggedWeeks = useMemo(
    () => new Set(forecast.filter((f) => isFlaggedWeek(f.band)).map((f) => f.weekStart)),
    [forecast],
  );
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const visibleWeeks = useMemo(
    () => (onlyFlagged && flaggedWeeks.size ? weekStarts.filter((w) => flaggedWeeks.has(w)) : weekStarts),
    [onlyFlagged, flaggedWeeks, weekStarts],
  );

  const gridW = LOAD_NAME_W + visibleWeeks.length * LOAD_WEEK_W;

  // ── Week paging (2026-07-26): floating ◀/▶ edge arrows scroll exactly one
  // more week into view (smooth), same affordance as the Phase-2/3 rails —
  // no hunting for the horizontal scrollbar.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateArrows); ro.disconnect(); };
  }, [updateArrows, visibleWeeks.length, users.length]);
  // ▶ at the right edge LOADS one more week (extends the data window), then the
  // effect below scrolls the fresh column into view once it arrives. Before the
  // edge it just pages through what's already loaded. Extension is disabled
  // while "only flagged" filters weeks — a fresh (unflagged) week would load
  // invisibly. ◀ pages back.
  const pendingExtendScroll = useRef(false);
  const canExtend = !!onExtendWeeks && !onlyFlagged;
  const scrollByWeek = useCallback((dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    if (dir === 1 && canExtend && el.scrollLeft >= el.scrollWidth - el.clientWidth - 8) {
      pendingExtendScroll.current = true;
      onExtendWeeks?.();
      return;
    }
    el.scrollBy({ left: dir * LOAD_WEEK_W, behavior: 'smooth' });
  }, [canExtend, onExtendWeeks]);
  useEffect(() => {
    if (!pendingExtendScroll.current) return;
    pendingExtendScroll.current = false;
    // Next frame: the new column is in the DOM — glide it into view.
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      el?.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
    });
  }, [visibleWeeks.length]);

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
    <Box ref={scrollRef} sx={{ overflow: 'auto', height: '100%' }}>
      {/* fit-content + right border closes the grid's right edge — without it
          the last week column ran open-ended into the page. */}
      <Box sx={{ minWidth: gridW, width: 'fit-content', position: 'relative', borderRight: `1px solid ${theme.palette.divider}` }}>
        <TeamWeekForecastStrip
          weekStarts={visibleWeeks} forecastByWeek={forecastByWeek}
          nameWidth={LOAD_NAME_W} weekWidth={LOAD_WEEK_W}
          onlyFlagged={onlyFlagged} onToggleOnlyFlagged={setOnlyFlagged}
          hasFlaggedWeeks={flaggedWeeks.size > 0}
        />

        {/* week header */}
        <Box sx={{ display: 'flex', position: 'sticky', top: FORECAST_STRIP_H, zIndex: 5, backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${theme.palette.primary.main}` }}>
          <Box sx={{ width: LOAD_NAME_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 6, backgroundColor: theme.palette.background.paper, px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Employee</Typography>
            <ToggleButtonGroup
              size="small" exclusive value={pillStyle}
              onChange={(_, v: WeekPillStyle | null) => changePillStyle(v)}
              sx={{ ml: 'auto', '& .MuiToggleButton-root': { py: 0, px: 1, fontSize: '0.58rem', fontWeight: 700, lineHeight: 1.6 } }}
            >
              <ToggleButton value="tile" title="Full-cell tiles with capacity heat">Tiles</ToggleButton>
              <ToggleButton value="barfill" title="Bars whose fill length is the busy %">Bars</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {visibleWeeks.map((ws) => {
            const flagged = isFlaggedWeek(forecastByWeek.get(ws)?.band ?? 'none');
            const weekEnd = addDaysISO(ws, 6);
            const statInWeek = holidays && [...holidays].some((h) => h >= ws && h <= weekEnd);
            return (
              <Box key={ws} sx={{ width: LOAD_WEEK_W, flexShrink: 0, textAlign: 'center', py: 0.75, borderLeft: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1 }}>Week of</Typography>
                <Typography sx={{ fontSize: '0.74rem', fontWeight: flagged ? 700 : 600 }}>
                  {weekOfMondayLabel(ws)}
                  {statInWeek && <Box component="span" sx={{ ml: 0.5, fontSize: '0.55rem', fontWeight: 800, color: theme.schedule.statWorked }}>◆</Box>}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* flat rows + accordions */}
        {users.map((u) => {
          const userBars = barsByUser.get(u.userKey) ?? [];
          const aggregates = buildPersonWeekLoads(userBars, visibleWeeks, holidays);
          const conflictWeeks = new Set(
            visibleWeeks.filter((ws) => capacityByKey.get(capacityMapKey(u.userKey, ws))?.hasOverbook === true),
          );
          const open = openKeys.has(u.userKey);
          return (
            <Box key={u.userKey}>
              <EmployeeWeekLoadRow
                user={u}
                aggregates={aggregates}
                conflictWeeks={conflictWeeks}
                pillStyle={pillStyle}
                open={open}
                onToggle={() => toggle(u.userKey)}
              />
              <Collapse in={open} timeout={280} unmountOnExit>
                <EmployeeAccordionPlan
                  bars={userBars.filter((b) => visibleWeeks.includes(b.weekStart))}
                  weekStarts={visibleWeeks}
                  onBarClick={onBarClick}
                />
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
    {/* Floating ◀/▶ arrows: ▶ pages, then LOADS more weeks at the edge — so it
        stays visible whenever extension is possible. */}
    <Phase2RailScrollArrows canScrollLeft={canScrollLeft} canScrollRight={canScrollRight || canExtend} onScroll={scrollByWeek} />
    </Box>
  );
};
